/**
 * Toasts — the successor to the Alpine `toast` store.
 *
 * Announced through an `aria-live` region so screen readers hear the outcome of
 * a mutation, not just sighted users.
 */
import { CheckCircle2, CircleAlert, Info, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from '../../lib/utils';

type ToastTone = 'success' | 'error' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  action?: ToastAction | undefined;
}

interface ToastContextValue {
  /** `action` — e.g. `{ label: 'Undo', onClick: () => ... }` — only for a
   * genuinely reversible outcome (a real reversal exists to call, not just a
   * second confirmation). Leave it off otherwise; a fake "Undo" that doesn't
   * actually undo anything is worse than no button at all. */
  success: (message: string, action?: ToastAction) => void;
  error: (message: string) => void;
  info: (message: string, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS = {
  success: CheckCircle2,
  error: CircleAlert,
  info: Info,
} as const;

const DURATION = 4500;
// Longer when there's an action to notice and click, not just read.
const ACTION_DURATION = 7000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string, action?: ToastAction) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, tone, message, action }]);
      // Errors stay until dismissed — they usually carry an action to take.
      if (tone !== 'error') {
        window.setTimeout(() => dismiss(id), action ? ACTION_DURATION : DURATION);
      }
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message, action) => push('success', message, action),
      error: (message) => push('error', message),
      info: (message, action) => push('info', message, action),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {toasts.map(({ id, tone, message, action }) => {
          const Icon = ICONS[tone];
          return (
            <div
              key={id}
              className={cn(
                'pointer-events-auto flex items-start gap-3 rounded-[var(--radius-card)] border p-3',
                'bg-surface-raised shadow-lg shadow-black/20',
                tone === 'success' && 'border-success/40',
                tone === 'error' && 'border-danger/50',
                tone === 'info' && 'border-border',
              )}
            >
              <Icon
                className={cn(
                  'mt-0.5 size-4 shrink-0',
                  tone === 'success' && 'text-success',
                  tone === 'error' && 'text-danger',
                  tone === 'info' && 'text-muted-foreground',
                )}
              />
              <p className="flex-1 text-sm">{message}</p>
              {action && (
                <button
                  type="button"
                  onClick={() => {
                    action.onClick();
                    dismiss(id);
                  }}
                  className="shrink-0 text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  {action.label}
                </button>
              )}
              <button
                type="button"
                onClick={() => dismiss(id)}
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Dismiss"
              >
                <X className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
